import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus,
  Church,
  MapPin,
  Phone,
  Mail,
  Loader2,
  Edit,
  Trash2,
  Users,
  Calendar,
  Crown,
  Zap,
  Gift
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const PIANO_CONFIG = {
  free: { label: 'Free', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: Gift },
  basic: { label: 'Basic', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Zap },
  pro: { label: 'Pro', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Crown },
};

const ChiesaForm = ({ chiesa, onSave, onClose }) => {
  const [formData, setFormData] = useState(chiesa || {
    nome: '',
    indirizzo: '',
    citta: '',
    referente: '',
    telefono: '',
    email: '',
    orari_culto: '',
    descrizione: '',
    sito_web: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData, chiesa?.id);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Chiesa / Associazione *</Label>
        <Input
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
          data-testid="chiesa-nome-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Indirizzo</Label>
          <Input
            value={formData.indirizzo}
            onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Città</Label>
          <Input
            value={formData.citta}
            onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Referente</Label>
        <Input
          value={formData.referente}
          onChange={(e) => setFormData({ ...formData, referente: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Telefono</Label>
          <Input
            value={formData.telefono}
            onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Sito Web</Label>
          <Input
            value={formData.sito_web}
            onChange={(e) => setFormData({ ...formData, sito_web: e.target.value })}
            placeholder="https://..."
          />
        </div>
        <div className="space-y-2">
          <Label>Orari di Culto</Label>
          <Input
            value={formData.orari_culto}
            onChange={(e) => setFormData({ ...formData, orari_culto: e.target.value })}
            placeholder="Dom 10:00-12:00"
          />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
        <Button type="submit" disabled={loading} data-testid="chiesa-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {chiesa ? 'Aggiorna' : 'Crea Ente'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const ChiesePage = () => {
  const { API, isSuperAdmin } = useAuth();
  const [chiese, setChiese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChiesa, setEditingChiesa] = useState(null);

  useEffect(() => {
    fetchChiese();
  }, []);

  const fetchChiese = async () => {
    try {
      const response = await axios.get(`${API}/chiese`);
      setChiese(response.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data, id) => {
    if (id) {
      await axios.put(`${API}/chiese/${id}`, data);
      toast.success('Ente aggiornato');
    } else {
      await axios.post(`${API}/chiese`, data);
      toast.success('Ente creato');
    }
    fetchChiese();
    setEditingChiesa(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo ente? Assicurati che non ci siano utenti o famiglie assegnate.')) return;
    try {
      await axios.delete(`${API}/chiese/${id}`);
      toast.success('Ente eliminato');
      fetchChiese();
    } catch (err) {
      toast.error(getErrorMessage(err, "Errore durante l'eliminazione"));
    }
  };

  const handlePianoChange = async (chiesaId, piano) => {
    try {
      await axios.put(`${API}/chiese/${chiesaId}/piano`, { piano });
      toast.success(`Piano aggiornato a ${PIANO_CONFIG[piano].label}`);
      fetchChiese();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore aggiornamento piano'));
    }
  };

  const openEdit = (c) => {
    setEditingChiesa(c);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingChiesa(null);
    setDialogOpen(true);
  };

  if (!isSuperAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Non hai i permessi per accedere a questa sezione.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="chiese-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Gestione Chiese / Associazioni</h1>
          <p className="text-muted-foreground">Amministra le chiese, le associazioni e i tenant della piattaforma</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <Button onClick={openCreate} data-testid="add-chiesa-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Ente
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingChiesa ? 'Modifica Ente' : 'Nuovo Ente'}
              </DialogTitle>
            </DialogHeader>
            <ChiesaForm
              chiesa={editingChiesa}
              onSave={handleSave}
              onClose={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Church className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Totale</p>
                <p className="text-xl font-heading font-bold">{chiese.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(PIANO_CONFIG).map(([key, conf]) => {
          const PIcon = conf.icon;
          const count = chiese.filter(c => (c.piano || 'free') === key).length;
          return (
            <Card key={key}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${conf.color}`}>
                    <PIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{conf.label}</p>
                    <p className="text-xl font-heading font-bold">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Chiese / Associazioni</CardTitle>
          <CardDescription>Tutte le chiese e associazioni registrate nella piattaforma</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : chiese.length === 0 ? (
            <div className="p-12 text-center">
              <Church className="w-12 h-12 mx-auto text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Nessun ente registrato</p>
              <Button className="mt-4" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi il primo ente
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Città</TableHead>
                  <TableHead className="hidden md:table-cell">Referente</TableHead>
                  <TableHead className="hidden lg:table-cell">Contatti</TableHead>
                  <TableHead>Piano</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {chiese.map((c) => (
                  <TableRow key={c.id} data-testid={`chiesa-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.nome}</div>
                      <div className="text-xs text-muted-foreground md:hidden">
                        {c.citta}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-muted-foreground" />
                        {c.citta || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {c.referente || '-'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="space-y-1">
                        {c.telefono && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-3 h-3" />
                            {c.telefono}
                          </div>
                        )}
                        {c.email && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={c.piano || 'free'} onValueChange={(v) => handlePianoChange(c.id, v)}>
                        <SelectTrigger className="w-[110px] h-8 text-xs" data-testid={`piano-select-${c.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">
                            <span className="flex items-center gap-1.5"><Gift className="w-3 h-3" /> Free</span>
                          </SelectItem>
                          <SelectItem value="basic">
                            <span className="flex items-center gap-1.5"><Zap className="w-3 h-3" /> Basic</span>
                          </SelectItem>
                          <SelectItem value="pro">
                            <span className="flex items-center gap-1.5"><Crown className="w-3 h-3" /> Pro</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.stato === 'attivo' ? 'default' : 'secondary'}>
                        {c.stato}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(c)}
                          data-testid={`edit-chiesa-${c.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDelete(c.id)}
                          data-testid={`delete-chiesa-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ChiesePage;
